import { Modal, Table, Button } from 'antd'
import { formatDisplayDateTime } from '../../utils/format'
import { formatDocumentStatus } from '../../utils/documentStatus'
import { KINGDEE_TAB_LINE_ITEMS_KEY } from '../../services/api'

type Props = {
  open: boolean
  loading?: boolean
  rows: Record<string, unknown>[]
  onClose: () => void
  onViewRow?: (row: Record<string, unknown>) => void
}

function fmtMoney(n: unknown) {
  if (n === '' || n === undefined || n === null) return '-'
  const x = Number(n)
  if (Number.isNaN(x)) return '-'
  return `¥${x.toLocaleString()}`
}

function isOutstockRow(row: Record<string, unknown>): boolean {
  if (row.formId === 'SAL_OUTSTOCK' || row._logisticsDocSource === 'sales_outstock') return true
  const name = String(row.bill_type_id_name ?? row.bill_type_id_fname ?? '').trim()
  if (name.includes('销售出库') || name.includes('出库')) return true
  const no = String(row.bill_no ?? row.billNo ?? '').trim().toUpperCase()
  return no.startsWith('XSCKD')
}

/** 出库单头 + 分录展开为一行一条物料明细 */
export function flattenOutstockDetailRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = []
  rows.filter(isOutstockRow).forEach((row, ri) => {
    const lines = row[KINGDEE_TAB_LINE_ITEMS_KEY]
    const billNo = String(row.bill_no ?? row.billNo ?? row.documentNo ?? '').trim()
    if (Array.isArray(lines) && lines.length > 0) {
      lines.forEach((line, li) => {
        const ln = line && typeof line === 'object' && !Array.isArray(line) ? (line as Record<string, unknown>) : {}
        out.push({
          ...row,
          ...ln,
          key: `${billNo || ri}-${li}`,
        })
      })
      return
    }
    out.push({ ...row, key: billNo || String(ri) })
  })
  return out
}

export default function OutstockDetailModal({ open, loading, rows, onClose, onViewRow }: Props) {
  const data = flattenOutstockDetailRows(rows)

  return (
    <Modal
      title="出库明细"
      open={open}
      onCancel={onClose}
      footer={<Button type="primary" onClick={onClose}>关闭</Button>}
      width={980}
      destroyOnClose
    >
      <Table
        size="small"
        loading={loading}
        pagination={false}
        rowKey={(r) => String((r as Record<string, unknown>).key ?? '')}
        scroll={{ x: 900 }}
        dataSource={data}
        locale={{ emptyText: '暂无该合同的出库明细' }}
        columns={[
          {
            title: '出库单号',
            dataIndex: 'bill_no',
            key: 'bill_no',
            width: 160,
            ellipsis: true,
            render: (_: unknown, row: Record<string, unknown>) =>
              String(row.bill_no ?? row.billNo ?? row.documentNo ?? '-').trim() || '-',
          },
          {
            title: '源单号',
            dataIndex: 'src_bill_no',
            key: 'src_bill_no',
            width: 150,
            ellipsis: true,
            render: (_: unknown, row: Record<string, unknown>) =>
              String(row.src_bill_no ?? row.srcBillNo ?? row.source_bill_no ?? '-').trim() || '-',
          },
          {
            title: '物料',
            key: 'material',
            width: 180,
            ellipsis: true,
            render: (_: unknown, row: Record<string, unknown>) =>
              String(
                row.material_id_name ?? row.material_name ?? row.material_id_fname ?? '-',
              ).trim() || '-',
          },
          {
            title: '数量',
            key: 'qty',
            width: 90,
            render: (_: unknown, row: Record<string, unknown>) => {
              const v = row.real_qty ?? row.qty ?? row.priceunitqty ?? row.base_unit_qty
              return v == null || v === '' ? '-' : String(v)
            },
          },
          {
            title: '金额',
            key: 'amount',
            width: 120,
            render: (_: unknown, row: Record<string, unknown>) =>
              fmtMoney(row.all_amount ?? row.amount ?? row.tax_amount),
          },
          {
            title: '日期',
            key: 'date',
            width: 160,
            render: (_: unknown, row: Record<string, unknown>) =>
              formatDisplayDateTime(
                row.date ?? row.bizDate ?? row.biz_date ?? row.delivery_date ?? row.create_date,
              ),
          },
          {
            title: '状态',
            key: 'status',
            width: 90,
            render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
          },
          ...(onViewRow
            ? [
                {
                  title: '操作',
                  key: 'op',
                  width: 90,
                  render: (_: unknown, row: Record<string, unknown>) => (
                    <Button type="link" size="small" onClick={() => onViewRow(row)}>
                      查看详情
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Modal>
  )
}
